<template>
  <div
    ref="triggerEl"
    class="trigger-wrapper"
    @mouseenter="onMouseEnter"
    @mouseleave="onMouseLeave"
    @focusin="onFocusIn"
    @focusout="onFocusOut"
  >
    <slot></slot>
  </div>

  <transition name="fade">
    <div
      v-if="visible"
      ref="tooltipEl"
      class="tooltiptext"
      :class="[finalPosition, size, { multiline: allowMultiLine }]"
      :style="[tooltipStyle, arrowVarStyle]"
    >
      {{ message }}
    </div>
  </transition>
</template>

<script setup lang="ts">
import type { CSSProperties } from 'vue';
import { nextTick, onBeforeUnmount, onMounted, ref } from 'vue';

interface Props {
  message: string;
  size?: 'large' | 'medium' | 'small';
  position?: 'top' | 'bottom' | 'left' | 'right';
  allowMultiLine?: boolean;
}

const props = withDefaults(defineProps<Props>(), {
  size: 'medium',
  position: 'top',
  allowMultiLine: false
});

const triggerEl = ref<HTMLElement | null>(null);
const tooltipEl = ref<HTMLElement | null>(null);

const visible = ref(false);
const finalPosition = ref(props.position);
const tooltipStyle = ref<CSSProperties>({
  position: 'fixed',
  top: '0px',
  left: '0px',
  transform: 'translate(-50%, -100%)',
  zIndex: '9999'
});
const arrowOffsetX = ref(0);
const arrowOffsetY = ref(0);
const arrowVarStyle = ref<CSSProperties>({});

function onMouseEnter() {
  showTooltip();
}
function onFocusIn() {
  showTooltip();
}
function onMouseLeave() {
  hideTooltip();
}
function onFocusOut() {
  hideTooltip();
}
function showTooltip() {
  visible.value = true;
  nextTick(() => positionTooltip());
}
function hideTooltip() {
  visible.value = false;
}

/**
 * Calculates the position of the tooltip and performs:
 *  - Vertical flip (top<->bottom) if it doesn't fit.
 *  - Horizontal correction if it exceeds the side edges.
 *  - Adjustment of the arrow to keep pointing to the element.
 */
function positionTooltip() {
  const trigger = triggerEl.value;
  const tooltip = tooltipEl.value;
  if (!trigger || !tooltip) return;

  const rect = trigger.getBoundingClientRect();
  const tooltipWidth = tooltip.offsetWidth;
  const tooltipHeight = tooltip.offsetHeight;
  finalPosition.value = props.position;

  const offset = 8;
  let top = 0;
  let left = 0;

  switch (finalPosition.value) {
    case 'top':
      top = rect.top - offset;
      left = rect.left + rect.width / 2;
      break;
    case 'bottom':
      top = rect.bottom + offset;
      left = rect.left + rect.width / 2;
      break;
    case 'left':
      top = rect.top + rect.height / 2;
      left = rect.left - offset;
      break;
    case 'right':
      top = rect.top + rect.height / 2;
      left = rect.right + offset;
      break;
  }

  if (finalPosition.value === 'top') {
    const topEdge = top - tooltipHeight;
    if (topEdge < 0) {
      finalPosition.value = 'bottom';
      top = rect.bottom + offset;
    }
  } else if (finalPosition.value === 'bottom') {
    const bottomEdge = top + tooltipHeight;
    if (bottomEdge > window.innerHeight) {
      finalPosition.value = 'top';
      top = rect.top - offset;
    }
  }

  let transform = '';
  switch (finalPosition.value) {
    case 'top':
      transform = 'translate(-50%, -100%)';
      break;
    case 'bottom':
      transform = 'translate(-50%, 0)';
      break;
    case 'left':
      transform = 'translate(-100%, -50%)';
      break;
    case 'right':
      transform = 'translate(0, -50%)';
      break;
  }

  arrowOffsetX.value = 0;
  arrowOffsetY.value = 0;

  if (finalPosition.value === 'top' || finalPosition.value === 'bottom') {
    const halfWidth = tooltipWidth / 2;
    const leftEdge = left - halfWidth;
    const rightEdge = left + halfWidth;

    if (leftEdge < 0) {
      const shift = -leftEdge;
      left += shift;
      arrowOffsetX.value = -shift;
    } else if (rightEdge > window.innerWidth) {
      const shift = window.innerWidth - rightEdge;
      left += shift;
      arrowOffsetX.value = -shift;
    }
  }

  tooltipStyle.value.top = `${top}px`;
  tooltipStyle.value.left = `${left}px`;
  tooltipStyle.value.transform = transform;

  arrowVarStyle.value = {
    '--arrow-offset-x': arrowOffsetX.value + 'px',
    '--arrow-offset-y': arrowOffsetY.value + 'px'
  };
}

function onScrollOrResize() {
  if (visible.value) {
    positionTooltip();
  }
}

onMounted(() => {
  window.addEventListener('scroll', onScrollOrResize, true);
  window.addEventListener('resize', onScrollOrResize, true);
});
onBeforeUnmount(() => {
  window.removeEventListener('scroll', onScrollOrResize, true);
  window.removeEventListener('resize', onScrollOrResize, true);
});
</script>

<style scoped lang="scss">
.tooltiptext {
  visibility: visible;
  background-color: $color-neutral-900;
  color: $color-text-white;
  text-align: center;
  border-radius: $radius-1;
  z-index: 9999;
  opacity: 1;
  transition: opacity 0.3s;
  box-shadow: $elevation-2;
  display: inline-block;
  white-space: nowrap;
  text-overflow: ellipsis;
  max-width: 50vw;

  &.large {
    padding: $space-2;
    font-size: $body-m-font-size;
    line-height: $body-m-line-height;
  }

  &.medium {
    padding: $space-2;
    font-size: $body-s-font-size;
    line-height: $body-s-line-height;
  }

  &.small {
    padding: $space-1 $space-2;
    font-size: $body-xs-font-size;
    line-height: $body-xs-line-height;
  }

  &.multiline {
    white-space: pre-wrap;
    word-wrap: break-word;
    overflow-wrap: break-word;
    text-overflow: unset;
    width: 50vw;
  }

  &.top::after {
    content: '';
    position: absolute;
    left: calc(50% + var(--arrow-offset-x, 0px));
    top: auto;
    bottom: 0;
    transform: translate(-50%, 100%);
    border-width: 5px;
    border-style: solid;
    border-color: $color-neutral-900 transparent transparent transparent;
  }

  &.bottom::after {
    content: '';
    position: absolute;
    left: calc(50% + var(--arrow-offset-x, 0px));
    top: 0;
    transform: translate(-50%, -100%);
    border-width: 5px;
    border-style: solid;
    border-color: transparent transparent $color-neutral-900 transparent;
  }

  &.left::after {
    content: '';
    position: absolute;
    top: calc(50% + var(--arrow-offset-y, 0px));
    right: 0;
    transform: translate(100%, -50%);
    border-width: 5px;
    border-style: solid;
    border-color: transparent transparent transparent $color-neutral-900;
  }

  &.right::after {
    content: '';
    position: absolute;
    top: calc(50% + var(--arrow-offset-y, 0px));
    left: 0;
    transform: translate(-100%, -50%);
    border-width: 5px;
    border-style: solid;
    border-color: transparent $color-neutral-900 transparent transparent;
  }
}

.fade-enter-active,
.fade-leave-active {
  transition: opacity 0.2s;
}

.fade-enter-from,
.fade-leave-to {
  opacity: 0;
}
</style>
